"use client";

import { useMemo, useState } from "react";
import { usePostHog } from "posthog-js/react";
import {
  RACE_DISTANCES,
  distanceByKey,
  formatDuration,
  formatPace,
  kmSplits,
  paceFor,
  parseDuration,
} from "@/lib/seo/pace";
import type { SeoLocale } from "@/lib/seo/config";

const COPY = {
  distance: { en: "Distance", pt: "Distância" },
  goalTime: { en: "Goal finish time", pt: "Tempo de chegada alvo" },
  goalHint: { en: "Format H:MM:SS or MM:SS", pt: "Formato H:MM:SS ou MM:SS" },
  calculate: { en: "Calculate pace", pt: "Calcular ritmo" },
  perKm: { en: "Pace per km", pt: "Ritmo por km" },
  perMile: { en: "Pace per mile", pt: "Ritmo por milha" },
  splits: { en: "Even splits", pt: "Splits constantes" },
  km: { en: "km", pt: "km" },
  cumulative: { en: "Cumulative time", pt: "Tempo acumulado" },
  invalid: {
    en: "Enter a valid time like 0:25:00",
    pt: "Insira um tempo válido como 0:25:00",
  },
};

export function PacingCalculator({ locale }: { locale: SeoLocale }) {
  const posthog = usePostHog();
  const [distanceKey, setDistanceKey] = useState("5k");
  const [time, setTime] = useState("00:25:00");
  const [submitted, setSubmitted] = useState<{
    seconds: number;
    meters: number;
  } | null>({ seconds: 1500, meters: 5000 });

  const t = (k: keyof typeof COPY) => COPY[k][locale];

  const error = useMemo(() => {
    if (time.trim() === "") return null;
    return parseDuration(time) === null ? COPY.invalid[locale] : null;
  }, [time, locale]);

  const onCalc = () => {
    const seconds = parseDuration(time);
    const dist = distanceByKey(distanceKey);
    if (seconds === null || !dist) return;
    setSubmitted({ seconds, meters: dist.meters });
    posthog?.capture("seo_tool_use", {
      seo_tool: "race-pace-calculator",
      seo_locale: locale,
      distance: distanceKey,
    });
  };

  const result = useMemo(() => {
    if (!submitted) return null;
    const { perKm, perMile } = paceFor(submitted.seconds, submitted.meters);
    return {
      perKm,
      perMile,
      splits: kmSplits(submitted.seconds, submitted.meters),
    };
  }, [submitted]);

  return (
    <div className="rounded-2xl border border-border bg-card p-6">
      <div className="grid sm:grid-cols-2 gap-4">
        <label className="block">
          <span className="text-sm font-medium">{t("distance")}</span>
          <select
            value={distanceKey}
            onChange={(e) => setDistanceKey(e.target.value)}
            className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2"
          >
            {RACE_DISTANCES.map((d) => (
              <option key={d.key} value={d.key}>
                {locale === "en" ? d.labelEn : d.labelPt}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="text-sm font-medium">{t("goalTime")}</span>
          <input
            type="text"
            inputMode="numeric"
            value={time}
            onChange={(e) => setTime(e.target.value)}
            placeholder="0:25:00"
            className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2"
          />
          <span className="text-xs text-muted-foreground">{t("goalHint")}</span>
        </label>
      </div>

      {error && <p className="mt-3 text-sm text-destructive">{error}</p>}

      <button
        type="button"
        onClick={onCalc}
        className="mt-5 inline-flex items-center rounded-md bg-primary px-5 py-2.5 font-medium text-primary-foreground hover:opacity-90"
      >
        {t("calculate")}
      </button>

      {result && (
        <div className="mt-8">
          <div className="grid grid-cols-2 gap-4">
            <div className="rounded-xl bg-muted/50 p-5 text-center">
              <div className="text-sm text-muted-foreground">{t("perKm")}</div>
              <div className="text-3xl font-bold mt-1">
                {formatPace(result.perKm)}
                <span className="text-base font-normal text-muted-foreground">
                  /km
                </span>
              </div>
            </div>
            <div className="rounded-xl bg-muted/50 p-5 text-center">
              <div className="text-sm text-muted-foreground">{t("perMile")}</div>
              <div className="text-3xl font-bold mt-1">
                {formatPace(result.perMile)}
                <span className="text-base font-normal text-muted-foreground">
                  /mi
                </span>
              </div>
            </div>
          </div>

          <h3 className="mt-8 mb-3 font-semibold">{t("splits")}</h3>
          <div className="overflow-hidden rounded-xl border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-left">
                <tr>
                  <th className="px-4 py-2 font-medium">{t("km")}</th>
                  <th className="px-4 py-2 font-medium">{t("cumulative")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {result.splits.map((sp) => (
                  <tr key={sp.km}>
                    <td className="px-4 py-2">{sp.km}</td>
                    <td className="px-4 py-2 font-mono">
                      {formatDuration(sp.cumulativeSeconds)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
